
import { useState, useEffect } from 'react';
import { Product } from '@/lib/supabase/products/types';
import { getProducts } from '@/lib/supabase/products';
import { useToast } from '@/components/ui/use-toast';
import { getCurrentUserOrganizationId } from '@/lib/supabase/products/organization';

export const useProductSelector = (
  onAddProduct: (product: any) => void,
  onClose: () => void,
  shopId?: string
) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  
  // For custom product input
  const [showCustomProduct, setShowCustomProduct] = useState(false);
  const [productName, setProductName] = useState('');
  const [productPrice, setProductPrice] = useState('');
  
  const { toast } = useToast();
  
  // Get organization ID first
  useEffect(() => {
    const getOrgId = async () => {
      try {
        const orgId = await getCurrentUserOrganizationId();
        if (orgId) {
          setOrganizationId(orgId);
          console.log("Retrieved organization ID for product selector:", orgId);
        }
      } catch (error) {
        console.error("Failed to get organization ID:", error);
      }
    };
    
    getOrgId();
  }, []);
  
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!organizationId) {
          console.log("Waiting for organization ID before fetching products");
          return;
        }
        
        console.log("Fetching products for shop:", shopId, "and organization:", organizationId);
        const fetchedProducts = await getProducts(shopId, organizationId);
        console.log("Products fetched:", fetchedProducts.length);
        
        if (Array.isArray(fetchedProducts)) {
          setProducts(fetchedProducts);
        } else {
          console.error("Invalid products data format:", fetchedProducts);
          setError("Format de données invalide");
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        setError("Erreur lors du chargement des produits");
        toast({
          title: "Erreur",
          description: "Impossible de charger les produits. Veuillez réessayer.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    if (organizationId) {
      fetchProducts();
    }
  }, [toast, shopId, organizationId]);
  
  const handleAddProduct = () => {
    if (selectedProduct) {
      // Add existing product - use discount_price if available and not zero
      const qty = parseInt(quantity);
      const productPrice = selectedProduct.discount_price && selectedProduct.discount_price > 0 
                         ? selectedProduct.discount_price 
                         : selectedProduct.price;
      
      if (!isNaN(qty) && qty > 0) {
        onAddProduct({
          id: selectedProduct.id,
          product_id: selectedProduct.id,
          name: selectedProduct.name,
          price: productPrice,
          discount_price: selectedProduct.discount_price,
          quantity: qty,
          total: productPrice * qty
        });
        
        // Reset form
        setSelectedProduct(null);
        setQuantity('1');
        onClose();
      }
    } else if (showCustomProduct && productName && productPrice) {
      // Add custom product with a custom ID
      const price = parseFloat(productPrice);
      const qty = parseInt(quantity);
      
      if (!isNaN(price) && !isNaN(qty) && qty > 0) {
        const customId = `custom-${Date.now()}`; // Generate a unique custom ID
        onAddProduct({
          id: customId,
          product_id: customId,
          name: productName,
          price: price,
          quantity: qty,
          total: price * qty
        });
        
        // Reset form
        setProductName('');
        setProductPrice('');
        setQuantity('1');
        setShowCustomProduct(false);
        onClose();
      }
    }
  };
  
  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return {
    products,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    quantity,
    setQuantity,
    selectedProduct,
    setSelectedProduct,
    showCustomProduct,
    setShowCustomProduct,
    productName,
    setProductName,
    productPrice,
    setProductPrice,
    filteredProducts,
    handleAddProduct
  };
};
